import { Aircraft } from "../models/aircraft";
import AirctaftSarEquipment, { emptyAircraftSarEquipment } from "../models/aircraftSar";

//Aircraft values that can be read back from a filed ICAO FPL message (the "ICAO FPL Code" of a flight plan).
//Per-flight values (E/ endurance, P/ persons, C/ PIC, route, times) are deliberately left out.
export type FplImportedAircraft = {
  message: string; //the FPL message found in the pasted text, normalized to one line
  aircraftId: string; //item 7 as filed: registration or callsign
  registration: string; //best-guess hyphenated, e.g. SX-ABC; the FPL itself carries it without the hyphen
  aircraft: Partial<Aircraft>;
};

//Field 18 indicators, so free text (e.g. RMK/) is only split on real keywords
const field18Keywords = [
  "STS", "PBN", "NAV", "COM", "DAT", "SUR", "DEP", "DEST", "DOF", "REG", "EET", "SEL", "TYP", "CODE",
  "DLE", "OPR", "ORGN", "PER", "ALTN", "RALT", "TALT", "RIF", "RMK", "RVR", "RFP", "IFP", "EUR",
];

function parseKeywordValues(text: string, keywordPattern: string): Record<string, string> {
  const values: Record<string, string> = {};
  const rgx = new RegExp(`(?:^|\\s)(${keywordPattern})\\/`, "g");
  const matches = Array.from(text.matchAll(rgx));
  matches.forEach((m, i) => {
    const start = (m.index ?? 0) + m[0].length;
    const end = i + 1 < matches.length ? matches[i + 1].index ?? text.length : text.length;
    values[m[1]] = text.slice(start, end).trim();
  });
  return values;
}

//Finds the FPL message in pasted text: with or without brackets, and inside a filing confirmation
//email (EuroFPL, AFTN copies) with addresses and other text around it.
export function extractFplMessage(text: string): string | undefined {
  const upper = text.toUpperCase();

  const bracketed = upper.match(/\(\s*FPL\s*-/);
  if (bracketed && bracketed.index !== undefined) {
    //Free text may itself contain brackets, so close on the matching one rather than the first
    let depth = 0;
    for (let i = bracketed.index; i < upper.length; i++) {
      if (upper[i] === "(") depth++;
      if (upper[i] === ")" && --depth === 0) {
        return upper.slice(bracketed.index + 1, i);
      }
    }
    return upper.slice(bracketed.index + 1);
  }

  const bare = upper.match(/\bFPL\s*-/);
  if (bare && bare.index !== undefined) {
    //Without brackets the message runs until a blank line
    return upper.slice(bare.index).split(/\n\s*\n/)[0];
  }
  return undefined;
}

//Nationality marks are one or two characters: D-EABC, G-ABCD, C-GABC vs SX-ABC, OE-ABC, CS-ABC, 9H-ABC
export function hyphenateRegistration(reg: string): string {
  if (reg.includes("-") || reg.length < 3) {
    return reg;
  }
  const singleCharPrefix = /^[BFGIMN]/.test(reg) || /^D[A-Z]/.test(reg) || /^C[FGI]/.test(reg);
  const prefixLength = singleCharPrefix ? 1 : 2;
  return `${reg.slice(0, prefixLength)}-${reg.slice(prefixLength)}`;
}

function parseSar(item19: string): AirctaftSarEquipment {
  const v = parseKeywordValues(item19, "[EPRSJDANC]");
  const sar: AirctaftSarEquipment = JSON.parse(JSON.stringify(emptyAircraftSarEquipment));

  if (v["R"] !== undefined) {
    sar.emergencyRadio = { elba: v["R"].includes("E"), vhf: v["R"].includes("V"), uhf: v["R"].includes("U") };
  }
  if (v["S"] !== undefined) {
    const s = v["S"];
    sar.survivalEquipment = { general: true, polar: s.includes("P"), desert: s.includes("D"), maritime: s.includes("M"), jungle: s.includes("J") };
  }
  if (v["J"] !== undefined) {
    const j = v["J"];
    sar.lifejackets = { general: true, light: j.includes("L"), fluores: j.includes("F"), uhf: j.includes("U"), vhf: j.includes("V") };
  }
  if (v["D"] !== undefined) {
    //D/2 6 C YELLOW → number, capacity, optional cover flag, optional colour
    const d = v["D"].match(/^(\d{1,2})\s+(\d{1,3})(?:\s+(C))?(?:\s+(.+))?$/);
    if (d) {
      sar.dinghies = { number: Number(d[1]), capacity: Number(d[2]), cover: d[3] === "C", colour: d[4] ?? "" };
    }
  }
  sar.aircraftColour = v["A"] ?? "";
  sar.remarks = v["N"] ?? "";
  return sar;
}

//Parses an ICAO FPL message out of pasted text: single line, the multi-line form shown on the FPL page,
//or a whole filing confirmation email.
export function parseAircraftFromFpl(fplText: string): FplImportedAircraft | never {
  const extracted = extractFplMessage(fplText);
  if (!extracted) {
    throw new Error("No ICAO FPL message found. It should contain (FPL-...");
  }

  const message = extracted
    .replace(/\s+/g, " ")
    .replace(/\s*-\s*/g, "-")
    .trim()
    .replace(/\)$/, "");

  const items = message.split("-");
  if (items.length < 9) {
    throw new Error("The FPL message looks incomplete. Please paste it from (FPL- up to the closing bracket.");
  }

  const [, item7, , item9, item10, , item15, , item18, item19 = ""] = items;

  const type = item9.match(/^(?:\d{1,2})?([A-Z][A-Z0-9]{1,3})\/([LMHJ])$/);
  if (!type) {
    throw new Error(`Could not read aircraft type and wake category from "${item9}".`);
  }

  const equipment = item10.match(/^([A-Z0-9]+)\/([A-Z0-9]+)$/);
  if (!equipment) {
    throw new Error(`Could not read equipment codes from "${item10}".`);
  }
  const [, equipmentCodes, surveillanceEquipmentCodes] = equipment;

  const f18 = item18 === "0" ? {} : parseKeywordValues(item18, field18Keywords.join("|"));

  //Item 7 may be a callsign; REG/ then carries the registration
  const aircraftId = item7.split("/")[0].trim();
  const registration = hyphenateRegistration((f18["REG"]?.split(" ")[0] ?? aircraftId).trim());

  const aircraft: Partial<Aircraft> = {
    icaoType: type[1] === "ZZZZ" && f18["TYP"] ? f18["TYP"] : type[1],
    wakeTurbulenceCategory: type[2] as Aircraft["wakeTurbulenceCategory"],
    equipmentCodes,
    surveillanceEquipmentCodes,
    isRvsmApproved: equipmentCodes.includes("W"),
    is833Capable: equipmentCodes.includes("Y"),
    sarEquipment: parseSar(item19),
  };

  const speed = item15.match(/^([NK])(\d{4})/);
  if (speed) {
    aircraft.cruiseSpeed = { unit: speed[1] as "N" | "K", value: Number(speed[2]) };
  }
  if (f18["PBN"]) aircraft.pbnCapabilities = f18["PBN"];
  if (f18["NAV"]) aircraft.field18Nav = f18["NAV"];
  if (f18["COM"]) aircraft.field18Com = f18["COM"];
  if (f18["DAT"]) aircraft.field18Dat = f18["DAT"];
  if (f18["RMK"]) aircraft.field18Remarks = f18["RMK"];
  if (f18["OPR"]) aircraft.operatorFullName = f18["OPR"];

  return { message: `(${message})`, aircraftId, registration, aircraft };
}
